import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { glassDeep } from '../../../../styles/components/common/glassmorphism';
import { Button, OutlineButton } from '../../../../styles/components/common/buttons';
import { useUI } from '../../../../contexts/UIContext';
import theme from '../../../../styles/theme';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${({ theme: propTheme }) => (propTheme || theme).zIndex.modal};
  padding: ${({ theme: propTheme }) => (propTheme || theme).spacing.md};
  backdrop-filter: blur(8px);
`;

const ModalContent = styled.div`
  ${glassDeep}
  border-radius: ${({ theme: propTheme }) => (propTheme || theme).borderRadius.lg};
  box-shadow: ${({ theme: propTheme }) => (propTheme || theme).shadows.xl};
  padding: ${({ theme: propTheme }) => (propTheme || theme).spacing.xl};
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
`;

const CloseButton = styled.button`
  position: relative;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.lg};
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
  z-index: 1;
  transition: all ${({ theme: propTheme }) => (propTheme || theme).transitions.fast};
  margin: ${({ theme: propTheme }) => (propTheme || theme).spacing.sm} 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    transform: scale(1.1);
  }
`;

const WorkshopTitle = styled.h2`
  margin: 0 0 ${({ theme: propTheme }) => (propTheme || theme).spacing.md};
  color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
  font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.xl};
  font-weight: ${({ theme: propTheme }) => (propTheme || theme).typography.fontWeight.semibold};
`;

const WorkshopStatus = styled.div`
  display: inline-block;
  padding: ${({ theme: propTheme }) => (propTheme || theme).spacing.xs} ${({ theme: propTheme }) => (propTheme || theme).spacing.md};
  background: ${({ status, theme: propTheme }) => {
    const currTheme = propTheme || theme;
    switch (status) {
      case 'completed':
        return currTheme.colors.gradientSuccess;
      case 'in-progress':
        return currTheme.colors.gradientPrimary;
      case 'recorded':
        return currTheme.colors.gradientPrimary;
      case 'registered':
        return currTheme.colors.gradientWarning;
      default:
        return 'rgba(158, 158, 158, 0.7)';
    }
  }};
  color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnPrimary};
  border-radius: ${({ theme: propTheme }) => (propTheme || theme).borderRadius.pill};
  font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.sm};
  font-weight: ${({ theme: propTheme }) => (propTheme || theme).typography.fontWeight.medium};
  box-shadow: ${({ theme: propTheme }) => (propTheme || theme).shadows.sm};
`;

const WorkshopInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme: propTheme }) => (propTheme || theme).spacing.xl};
  margin-bottom: ${({ theme: propTheme }) => (propTheme || theme).spacing.xl};
`;

const InfoItem = styled.div`
  h4 {
    margin: 0 0 ${({ theme: propTheme }) => (propTheme || theme).spacing.xs};
    color: ${({ theme: propTheme }) => (propTheme || theme).colors.textSecondary};
    font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.sm};
    font-weight: ${({ theme: propTheme }) => (propTheme || theme).typography.fontWeight.medium};
  }
  
  p {
    margin: 0;
    color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
    font-weight: ${({ theme: propTheme }) => (propTheme || theme).typography.fontWeight.medium};
  }
`;

const Description = styled.div`
  margin-bottom: ${({ theme: propTheme }) => (propTheme || theme).spacing.xl};
  h3 {
    color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
    font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.lg};
    margin-bottom: ${({ theme: propTheme }) => (propTheme || theme).spacing.sm};
    font-weight: ${({ theme: propTheme }) => (propTheme || theme).typography.fontWeight.semibold};
  }
  p {
    color: rgba(255, 255, 255, 0.8);
    line-height: ${({ theme: propTheme }) => (propTheme || theme).typography.lineHeight.normal};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme: propTheme }) => (propTheme || theme).spacing.sm};
  margin-top: ${({ theme: propTheme }) => (propTheme || theme).spacing.md};
`;

const ActionButton = styled(Button)`
  flex: 1;
`;

const SecondaryActionButton = styled(OutlineButton)`
  flex: 1;
`;

const WorkshopModal = ({ 
  workshop, 
  onClose, 
  onRegister, 
  onUnregister,
  onJoin,
  isRegistered,
  completedData 
}) => {
  const [isRegistering, setIsRegistering] = useState(false);
  const { setWorkshopModalOpen } = useUI();
  
  useEffect(() => {
    // Set modal open when component mounts
    setWorkshopModalOpen(true);
    
    // Set modal closed when component unmounts
    return () => setWorkshopModalOpen(false);
  }, [setWorkshopModalOpen, workshop.id]);

  const handleRegister = async () => {
    setIsRegistering(true);
    await onRegister(workshop);
    setIsRegistering(false);
  };

  const handleUnregister = () => {
    onUnregister(workshop);
  };

  const handleJoinSession = () => {
    if (workshop.meetingLink) {
      onJoin(workshop);
    }
  };

  const getWorkshopStatus = () => {
    if (completedData && workshop.type !== 'live') return 'completed';
    
    const now = new Date();
    const [startTime] = workshop.time.split(' - ');
    const workshopDate = new Date(`${workshop.date} ${startTime}`);
    if (workshop.type === 'live') {
      return 'in-progress';
    }
    
    if (workshop.type === 'recorded' || workshop.type === 'pre-recorded') {
      return 'recorded';
    }
    if (now < workshopDate) {
      return 'upcoming';
    }
    
    return 'completed';
  };

  const status = getWorkshopStatus();
  const [startTime, endTime] = workshop.time.split(' - ');

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <WorkshopTitle>{workshop.title}</WorkshopTitle>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <WorkshopStatus status={status}>
            {status === 'in-progress' ? 'In Progress' : 
             status.charAt(0).toUpperCase() + status.slice(1)}
          </WorkshopStatus>
          {isRegistered && (
            <WorkshopStatus status="registered">
              Registered
            </WorkshopStatus>
          )}
        </div>
        <CloseButton onClick={onClose}>&times;</CloseButton>

        <WorkshopInfo>
          <InfoItem>
            <h4>Category</h4>
            <p>{workshop.category}</p>
          </InfoItem>
          <InfoItem>
            <h4>Date</h4>
            <p>{new Date(workshop.date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}</p>
          </InfoItem>
          <InfoItem>
            <h4>Time</h4>
            <p>{startTime} - {endTime}</p>
          </InfoItem>
          <InfoItem>
            <h4>Duration</h4>
            <p>{workshop.duration}</p>
          </InfoItem>
          <InfoItem>
            <h4>Instructor</h4>
            <p>{workshop.presenter}</p>
          </InfoItem>
          {workshop.price !== undefined && (
            <InfoItem>
              <h4>Price</h4>
              <p>{workshop.price === 0 ? 'Free' : `$${workshop.price}`}</p>
            </InfoItem>
          )}
        </WorkshopInfo>

        <Description>
          <h3>About this Workshop</h3>
          <p>{workshop.description}</p>
          
          {workshop.presenterBio && (
            <>
              <h3>About the Instructor</h3>
              <p>{workshop.presenterBio}</p>
            </>
          )}
        </Description>

        <ButtonGroup>
          {status === 'upcoming' && !isRegistered && (
            <ActionButton
              onClick={handleRegister}
              disabled={isRegistering || workshop.currentAttendees >= workshop.maxAttendees}
            >
              {isRegistering ? 'Registering...' : 
               workshop.currentAttendees >= workshop.maxAttendees ? 'Workshop Full' : 
               'Register Now'}
            </ActionButton>
          )}
          
          {status === 'upcoming' && isRegistered && (
            <SecondaryActionButton onClick={handleUnregister}>
              Unregister
            </SecondaryActionButton>
          )}
          
          {status === 'live' && isRegistered && (
            <ActionButton onClick={handleJoinSession}>
              Join Live Session
            </ActionButton>
          )}
          
          {status === 'recorded' && workshop.videoUrl && (
            <ActionButton onClick={() => onJoin(workshop)}>
              Watch Recording
            </ActionButton>
          )}
          
          <SecondaryActionButton onClick={onClose}>Close</SecondaryActionButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

export default WorkshopModal;
